import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert,
  Grid,
  CircularProgress,
} from '@mui/material';
import { toast } from 'react-toastify';
import Layout from '../../components/Layout';
import { paymentAPI, authAPI } from '../../services/api';

const BuyCredits = () => {
  const [credits, setCredits] = useState('');
  const [rate, setRate] = useState(1);
  const [gst, setGst] = useState(18);
  const [loading, setLoading] = useState(false);
  const [loadingRate, setLoadingRate] = useState(true);
  const [user, setUser] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [rateRes, gstRes, profileRes] = await Promise.all([
        paymentAPI.getCreditRate(),
        paymentAPI.getGSTRate().catch(() => ({ data: { gst: 18 } })),
        authAPI.getProfile()
      ]);
      setRate(rateRes.data.rate);
      setGst(gstRes.data.gst);
      setUser(profileRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoadingRate(false);
    }
  };

  const getBaseAmount = () => {
    const c = parseFloat(credits) || 0;
    return c * rate;
  };

  const getGSTAmount = () => {
    return getBaseAmount() * (gst / 100);
  };

  const getTotalAmount = () => {
    return getBaseAmount() + getGSTAmount();
  };

  const handlePayment = async () => {
    const creditAmount = parseFloat(credits);
    if (!creditAmount || creditAmount <= 0) {
      toast.error('Enter a valid number of credits');
      return;
    }

    setLoading(true);
    try {
      // Create order
      const orderRes = await paymentAPI.createOrder(creditAmount);
      const { orderId, amount, currency } = orderRes.data;

      // Open Razorpay checkout
      const options = {
        key: process.env.REACT_APP_RAZORPAY_KEY_ID,
        amount: amount,
        currency: currency,
        name: 'KYC Suite',
        description: `Purchase ${creditAmount} Credits`,
        order_id: orderId,
        handler: async function (response) {
          try {
            const verifyRes = await paymentAPI.verifyPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            });
            toast.success(verifyRes.message || 'Credits added successfully!');
            setCredits('');
            fetchData();
          } catch (error) {
            toast.error(error.response?.data?.message || 'Payment verification failed');
          }
        },
        prefill: {
          name: user ? `${user.firstName} ${user.lastName}` : '',
          email: user?.email || '',
          contact: user?.phoneNumber || ''
        },
        theme: {
          color: '#667eea'
        },
        modal: {
          ondismiss: function () {
            setLoading(false);
            toast.info('Payment cancelled');
          }
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.on('payment.failed', function (response) {
        toast.error(response.error.description || 'Payment failed');
        setLoading(false);
      });
      rzp.open();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create order');
    } finally {
      setLoading(false);
    }
  };

  if (loadingRate) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
          Buy Credits
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          Purchase credits to use verification services. Current rate: ₹{rate} per credit.
        </Typography>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Purchase Credits</Typography>
              
              <Alert severity="info" sx={{ mb: 2 }}>
                1 Credit = ₹{rate} + {gst}% GST = ₹{(rate * (1 + gst / 100)).toFixed(2)}
              </Alert>

              <TextField
                fullWidth
                label="Number of Credits"
                type="number"
                value={credits}
                onChange={(e) => setCredits(e.target.value)}
                placeholder="Enter credits to purchase"
                sx={{ mb: 2 }}
                inputProps={{ min: 1 }}
              />

              {credits && parseFloat(credits) > 0 && (
                <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#f8faf8' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">Subtotal ({credits} × ₹{rate})</Typography>
                    <Typography variant="body2">₹{getBaseAmount().toFixed(2)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2" color="text.secondary">GST ({gst}%)</Typography>
                    <Typography variant="body2">₹{getGSTAmount().toFixed(2)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: '1px solid #e0e0e0' }}>
                    <Typography variant="body1" fontWeight={700}>Total</Typography>
                    <Typography variant="body1" fontWeight={700} color="primary.main">₹{getTotalAmount().toFixed(2)}</Typography>
                  </Box>
                </Paper>
              )}

              <Button
                variant="contained"
                fullWidth
                size="large"
                onClick={handlePayment}
                disabled={loading || !credits || parseFloat(credits) <= 0}
                sx={{ 
                  py: 1.5,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : `Pay ₹${credits ? getTotalAmount().toFixed(2) : '0.00'}`}
              </Button>
            </Paper>
          </Grid>

          {/* Balance section only */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>Your Balance</Typography>
              <Typography variant="h3" sx={{ color: '#667eea', fontWeight: 700 }}>
                {user?.credits?.remaining?.toFixed(2) || '0.00'}
              </Typography>
              <Typography variant="body2" color="text.secondary">Credits Remaining</Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Layout>
  );
};

export default BuyCredits;
